import React, { useCallback, useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import type { LocationMarker } from '@fhnw/types/location';
import { MapState } from '../LocationMapContent';

type UseMapManagerProps = {
  locationsData: LocationMarker[];
  state: MapState;
  dispatch: React.Dispatch<any>;
  onChange: (location?: LocationMarker) => void;
  mapInstance: mapboxgl.Map;
};

const SINGLE_ZOOM_LEVEL = 15;
const BOUNDS_PADDING = 100;
const MARKER_BASE_CLASS = 'mapboxgl-marker mapboxgl-marker-anchor-center';

const useMapManager = ({
  locationsData,
  state,
  dispatch,
  onChange,
  mapInstance,
}: UseMapManagerProps) => {
  const markersRef = useRef<mapboxgl.Marker[]>([]);
  const currentMarkerRef = useRef<mapboxgl.Marker | null>(null);
  const boundsRef = useRef(new mapboxgl.LngLatBounds());

  const handleFlyToLocation = useCallback(
    (
      location: LocationMarker,
      zoomLevel?: number,
      triggeredByClick: boolean = false,
    ) => {
      if (!state.map) return;

      state.map.flyTo({
        center: [location.longitude, location.latitude],
        zoom: zoomLevel,
      });

      if (triggeredByClick)
        dispatch({ type: 'SET_CLICK_TRIGGERED', payload: false });
    },
    [dispatch, state.map],
  );

  const fitBoundsToMarkers = useCallback(() => {
    if (!state.map || boundsRef.current.isEmpty()) return;

    state.map.fitBounds(boundsRef.current, { padding: BOUNDS_PADDING });
    dispatch({ type: 'SET_FIT_BOUNDS', payload: true });
  }, [dispatch, state.map]);

  const findMatchingMarker = (location: LocationMarker) =>
    markersRef.current.find(
      (marker) =>
        marker.getLngLat().lng === location.longitude &&
        marker.getLngLat().lat === location.latitude,
    );

  const createMarker = (location: LocationMarker) => {
    const markerElement = document.createElement('div');
    const marker = new mapboxgl.Marker({ element: markerElement })
      .setLngLat([location.longitude, location.latitude])
      .addTo(mapInstance);

    markerElement.addEventListener('click', () => {
      handleLocationClick(location);
    });

    return marker;
  };

  const handleLocationClick = useCallback(
    (location: LocationMarker) => {
      const matchingMarker = findMatchingMarker(location);

      if (!matchingMarker) return;

      setMarkerClass(currentMarkerRef.current, false);

      if (currentMarkerRef.current === matchingMarker || !matchingMarker) {
        // Zoom out (second click)
        fitBoundsToMarkers();
        currentMarkerRef.current = null;
        onChange?.();
      } else {
        // Zoom in (first click)
        handleFlyToLocation(location, SINGLE_ZOOM_LEVEL, true);
        dispatch({
          type: 'SET_LOCATION_AND_BOUNDS',
          payload: { location, fitBounds: false },
        });
        currentMarkerRef.current = matchingMarker;
        setMarkerClass(matchingMarker, true);
        onChange?.(location);
      }
    },
    [onChange, dispatch, fitBoundsToMarkers, handleFlyToLocation],
  );

  const setMarkerClass = (
    marker: mapboxgl.Marker | null,
    isActive: boolean,
  ) => {
    if (!marker) return;

    marker.getElement().className = isActive
      ? `${MARKER_BASE_CLASS} active-marker`
      : MARKER_BASE_CLASS;
  };

  useEffect(() => {
    markersRef.current.forEach((marker) => marker.remove());
    markersRef.current = [];
    boundsRef.current = new mapboxgl.LngLatBounds();

    if (!state.map || locationsData.length === 0) return;

    locationsData.forEach((loc) => {
      const marker = createMarker(loc);
      markersRef.current.push(marker);
      boundsRef.current.extend([loc.longitude, loc.latitude]);

      const isActiveLocation =
        loc.longitude === state.activeLocation?.longitude &&
        loc.latitude === state.activeLocation?.latitude;

      if (isActiveLocation) {
        currentMarkerRef.current = marker;
        setMarkerClass(marker, true);
      }
    });

    if (!state.activeLocation) return;

    handleFlyToLocation(state.activeLocation, SINGLE_ZOOM_LEVEL);
    dispatch({
      type: 'SET_LOCATION_AND_BOUNDS',
      payload: { location: state.activeLocation, fitBounds: false },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.map, state.activeLocation, locationsData]);

  return { handleLocationClick };
};

export default useMapManager;
